import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowLeft,
  Edit, 
  Trash2, 
  Search,
  Filter,
  MoreVertical,
  Users,
  User,
  Mail,
  Building,
  Calendar,
  Shield,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { Row, Col, Button, Form, Table, Card, Badge, InputGroup, Dropdown, Modal, Alert, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { useUser } from '../contexts/UserContext';
import './Dashboard.css';
import './CompanyUsers.css';

const CompanyUsers = () => {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { user } = useUser();
  
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [sortColumn, setSortColumn] = useState(null); // 'firstName', 'lastName', 'email', 'department', 'status'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    department: '',
    role: '',
    bio: '',
    isActive: true
  });
  const stateRestoreInProgress = useRef(false);
  const STORAGE_KEY_BASE = 'companyUsersState';
  const hasRestoredState = useRef(false);

  const coerceNumber = (val, fallback) => {
    const n = typeof val === 'number' ? val : Number(val);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };

  useEffect(() => {
    // Force Poppins font on all elements in this component
    const style = document.createElement('style');
    style.textContent = `
      .company-users-wrapper *,
      .company-users-wrapper *::before,
      .company-users-wrapper *::after {
        font-family: 'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif !important;
      }
    `;
    document.head.appendChild(style);

    // Fetch company and users data
    fetchCompanyData();
    fetchUsersData();

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, [companyId]);

  // Restore saved filters/sort for this company from session storage
  useEffect(() => {
    const storageKey = `${STORAGE_KEY_BASE}:${companyId}`;
    const savedRaw = sessionStorage.getItem(storageKey);
    if (!savedRaw) return;
    if (hasRestoredState.current) return;
    hasRestoredState.current = true;
    try {
      const saved = JSON.parse(savedRaw);
      stateRestoreInProgress.current = true;
      if (typeof saved.searchTerm === 'string') setSearchTerm(saved.searchTerm);
      if (typeof saved.filterDepartment === 'string') setFilterDepartment(saved.filterDepartment);
      if (typeof saved.sortColumn === 'string' || saved.sortColumn === null) setSortColumn(saved.sortColumn);
      if (typeof saved.sortDirection === 'string') setSortDirection(saved.sortDirection);
    } catch (err) {
      console.error('Failed to restore company users state', err);
    } finally {
      setTimeout(() => {
        stateRestoreInProgress.current = false;
      }, 0);
    }
  }, [companyId]);

  // Persist current filters/sort to session storage
  useEffect(() => {
    const storageKey = `${STORAGE_KEY_BASE}:${companyId}`;
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          searchTerm: typeof searchTerm === 'string' ? searchTerm : '',
          filterDepartment: typeof filterDepartment === 'string' ? filterDepartment : 'all',
          sortColumn,
          sortDirection
        })
      );
    } catch (err) {
      console.error('Failed to persist company users state', err);
    }
  }, [companyId, searchTerm, filterDepartment, sortColumn, sortDirection]);

  const fetchCompanyData = async () => {
    try {
      const response = await apiService.getCompanyById(companyId);
      console.log('Company API Response:', response);
      
      if (response.success) {
        console.log('Setting company from response.data:', response.data);
        setCompany({
          id: response.data.id,
          identificationNumber: response.data.identificationNo || `COMP${response.data.id.toString().padStart(3, '0')}`,
          name: response.data.company || 'Unknown Company',
          industry: response.data.branch || 'Unknown Industry',
          status: response.data.active ? 'Active' : 'Inactive'
        });
      } else {
        console.log('Company fetch failed:', response.message);
        setError(response.message || 'Failed to load company information');
      }
    } catch (error) {
      console.error('Error fetching company:', error);
      setError('Failed to load company information');
    }
  };

  const fetchUsersData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching users for companyId:', companyId);
      
      const response = await apiService.getUsersByAssetId(companyId);
      
      console.log('Users API Response:', response);
      console.log('Response type:', typeof response);
      console.log('Response keys:', Object.keys(response || {}));
      
      // The API returns: { status: true, message: '...', data: [...] }
      if (response.status && Array.isArray(response.data)) {
        console.log('Setting users from response.data:', response.data);
        setUsers(response.data);
      } else if (Array.isArray(response)) {
        console.log('Setting users from direct array:', response);
        setUsers(response);
      } else {
        console.log('No valid users data found, setting empty array');
        console.log('Response structure:', JSON.stringify(response, null, 2));
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(error.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (userId) => {
    setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
  };

  const handleEditUser = (user) => {
    console.log('Editing user:', user);
    setEditingUser(user);
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      department: user.department || '',
      role: user.role || '',
      bio: user.bio || '',
      isActive: user.isActive !== false // Default to true if not specified
    });
    setShowEditModal(true);
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    
    try {
      console.log('Saving user with data:', editForm);
      
      // Call API to update user profile
      const response = await apiService.updateUserProfile(editingUser.id, {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        department: editForm.department,
        role: editForm.role,
        bio: editForm.bio
      });
      
      console.log('Update response:', response);
      
      if (response.success || response.status) {
        // Update local state
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === editingUser.id 
              ? { ...user, ...editForm }
              : user
          )
        );
        
        // Close modal
        setShowEditModal(false);
        setEditingUser(null);
        
        console.log('User updated successfully');
      } else {
        console.error('Failed to update user:', response.message);
        setError(response.message || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setError(error.message || 'Failed to update user');
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingUser(null);
  };

  // Filter and search users
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDepartment = filterDepartment === 'all' || 
      (user.department?.toLowerCase() === filterDepartment.toLowerCase());
    
    return matchesSearch && matchesDepartment;
  });

  // Handle column header click for sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sort icon for a column
  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown size={16} className="me-1 text-muted" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp size={16} className="me-1 text-primary" />
      : <ArrowDown size={16} className="me-1 text-primary" />;
  };

  // Sort users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (!sortColumn) return 0;

    let aValue, bValue;

    switch (sortColumn) {
      case 'firstName':
        aValue = (a.firstName || '').toLowerCase();
        bValue = (b.firstName || '').toLowerCase();
        break;
      case 'lastName':
        aValue = (a.lastName || '').toLowerCase();
        bValue = (b.lastName || '').toLowerCase();
        break;
      case 'email':
        aValue = (a.email || '').toLowerCase();
        bValue = (b.email || '').toLowerCase();
        break;
      case 'department':
        aValue = (a.department || '').toLowerCase();
        bValue = (b.department || '').toLowerCase();
        break;
      case 'status':
        // Sort by active status (active first)
        const aActive = a.active !== false && a.active !== 0;
        const bActive = b.active !== false && b.active !== 0;
        if (aActive === bActive) return 0;
        return sortDirection === 'asc' 
          ? (aActive ? -1 : 1)
          : (aActive ? 1 : -1);
      default:
        return 0;
    }

    // For non-status columns, compare string values
    if (sortColumn !== 'status') {
      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
    }
    return 0;
  });

  const getDepartmentBadge = (department) => {
    const colors = {
      'Engineering': 'primary',
      'Sales': 'success',
      'Operations': 'info',
      'Marketing': 'warning',
      'HR': 'secondary',
      'Finance': 'danger'
    };
    return <Badge bg={colors[department] || 'light'} text="dark">{department}</Badge>;
  };

  const getStatusBadge = (user) => {
    if (user.active === false || user.active === 0) {
      return <Badge bg="danger">Inactive</Badge>;
    }
    return <Badge bg="success">Active</Badge>;
  };

  // Debug logging
  console.log('CompanyUsers render - users:', users);
  console.log('CompanyUsers render - filteredUsers:', filteredUsers);
  console.log('CompanyUsers render - loading:', loading);
  console.log('CompanyUsers render - error:', error);

  // Loading state
  if (loading) {
    return (
      <div className="company-users-wrapper">
        <div className="modern-dashboard">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading users...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="company-users-wrapper">
      <div className="modern-dashboard">
        {/* Error Alert */}
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {/* Header */}
        <div className="dashboard-header-section mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="d-flex align-items-center mb-2">
                {user?.role === 'superAdmin' && (
                  <Button 
                    variant="link" 
                    className="p-0 me-3 text-decoration-none text-dark"
                    style={{ color: '#1f2937' }}
                    onClick={() => navigate('/companies')}
                  >
                    <ArrowLeft size={20} />
                  </Button>
                )}
                <div>
                  <h1 className="dashboard-title mb-1">{company?.name || 'Loading...'}</h1>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="nav-tabs-custom">
          <div className="nav-item">
            <span 
              className="nav-link"
              onClick={() => navigate(`/company/${companyId}/projects`)}
              style={{ cursor: 'pointer' }}
            >
              Projects
            </span>
          </div>
          <div className="nav-item">
            <span 
              className="nav-link"
              onClick={() => navigate(`/company/${companyId}/retailers`)}
              style={{ cursor: 'pointer' }}
            >
              Retail
            </span>
          </div>
          <div className="nav-item active">
            <span className="nav-link active">Users</span>
          </div>
        </div>

        {/* Filters and Search */}
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={4}>
                <InputGroup>
                  <InputGroup.Text>
                    <Search size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={filterDepartment}
                  onChange={(e) => setFilterDepartment(e.target.value)}
                >
                  <option value="all">All Departments</option>
                  <option value="engineering">Engineering</option>
                  <option value="sales">Sales</option>
                  <option value="operations">Operations</option>
                  <option value="marketing">Marketing</option>
                  <option value="hr">HR</option>
                  <option value="finance">Finance</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Button variant="outline-secondary" className="w-100">
                  <Filter size={16} className="me-2" />
                  Filters
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Users Table */}
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Users List</h5>
                <span className="text-muted">{sortedUsers.length} users found</span>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive className="modern-table mb-0">
              <thead>
                <tr>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('firstName')}
                  >
                    <div className="d-flex align-items-center">
                      {getSortIcon('firstName')}
                      First Name
                    </div>
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('lastName')}
                  >
                    <div className="d-flex align-items-center">
                      {getSortIcon('lastName')}
                      Last Name
                    </div>
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('email')}
                  >
                    <div className="d-flex align-items-center">
                      {getSortIcon('email')}
                      Email
                    </div>
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('department')}
                  >
                    <div className="d-flex align-items-center">
                      {getSortIcon('department')}
                      Department
                    </div>
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('status')}
                  >
                    <div className="d-flex align-items-center">
                      {getSortIcon('status')}
                      Status
                    </div>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-5">
                      <Users size={48} className="text-muted mb-3" />
                      <h5 className="text-muted">No users found</h5>
                      <p className="text-muted">This company doesn't have any registered users yet.</p>
                    </td>
                  </tr>
                ) : (
                  sortedUsers.map((user) => (
                    <tr key={user.id} style={{ cursor: 'pointer' }}>
                      <td>
                        <span>{user.firstName || 'Unknown'}</span>
                      </td>
                      <td>{user.lastName || 'Unknown'}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <Mail size={16} className="me-2 text-muted" />
                          {user.email ? (
                            <a 
                              href={`mailto:${user.email}`} 
                              style={{ color: '#0d6efd', textDecoration: 'none' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {user.email}
                            </a>
                          ) : (
                            <span className="text-muted">N/A</span>
                          )}
                        </div>
                      </td>
                      <td>{getDepartmentBadge(user.department || 'N/A')}</td>
                      <td>{getStatusBadge(user)}</td>
                      <td>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => handleEditUser(user)}
                        >
                          <Edit size={14} className="me-1" />
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
            
            {/* Record Count */}
            <div className="text-center py-3 border-top">
              <span className="text-muted">{sortedUsers.length} records</span>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Edit User Modal */}
      <Modal show={showEditModal} onHide={handleCancelEdit} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.firstName}
                    onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.lastName}
                    onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    value={editForm.email}
                    disabled
                    className="bg-light"
                  />
                  <Form.Text className="text-muted">Email address cannot be changed</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    value={editForm.role}
                    onChange={(e) => setEditForm(prev => ({ ...prev, role: e.target.value }))}
                  >
                    <option value="user">User</option>
                    <option value="companyAdmin">Company Admin</option>
                    <option value="superAdmin">Super Admin</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Department</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.department}
                    onChange={(e) => setEditForm(prev => ({ ...prev, department: e.target.value }))}
                    placeholder="Enter department"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Active User"
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Bio</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={editForm.bio}
                onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Enter user bio..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelEdit}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveUser}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CompanyUsers;
